import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { ApQboBillLine } from "@/accounts-payable/types";
import { formatCentsOrDash } from "@/core/utils/money";
import { RecordField } from "./record-fields";

/** How many lines show before "Show all", as in `LineItemList`. */
const LINE_PREVIEW = 3;

/**
 * A proposed bill's expense lines with their amounts, then the total. Laid
 * out like the PO line items (`LineItemList`), whose shape these don't share.
 */
export const QboBillLines = ({
  lines,
  totalCents,
}: {
  lines: ApQboBillLine[];
  totalCents: number;
}) => {
  const [showAll, setShowAll] = useState(false);
  const overflow = lines.length > LINE_PREVIEW;
  const visible = showAll || !overflow ? lines : lines.slice(0, LINE_PREVIEW);

  return (
    <RecordField label={`Lines (${lines.length})`} fullWidth>
      <Stack className="w-full" align="start" gap={2}>
        <Stack align="start" gap={0} className="w-full divide-y divide-line">
          {visible.map((line, i) => (
            <Stack
              key={i}
              direction="row"
              justify="between"
              gap={4}
              align="start"
              className="w-full py-2 first:pt-0"
            >
              <Text className="min-w-0 wrap-break-word">
                {line.description}
              </Text>
              <Text className="flex-none tabular-nums">
                {formatCentsOrDash(line.amount_cents)}
              </Text>
            </Stack>
          ))}
          <Stack
            direction="row"
            justify="between"
            gap={4}
            align="start"
            className="w-full pt-2"
          >
            <Text variant="label">Total</Text>
            <Text variant="label" className="tabular-nums">
              {formatCentsOrDash(totalCents)}
            </Text>
          </Stack>
        </Stack>
        {overflow && (
          <Button
            variant="link"
            size="sm"
            aria-expanded={showAll}
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "Show fewer" : `Show all ${lines.length}`}
          </Button>
        )}
      </Stack>
    </RecordField>
  );
};
